import { uuidv7 } from "uuidv7"

import type { PropertyFilterType } from "~/classes/generated/interface"

import ColumnMode from "~/assets/icons/viewmode/column.svg?component"
import DetailMode from "~/assets/icons/viewmode/detail.svg?component"
import FeedMode from "~/assets/icons/viewmode/feed.svg?component"
import GalleryMode from "~/assets/icons/viewmode/gallery.svg?component"
import GridMode from "~/assets/icons/viewmode/grid.svg?component"
import IconMode from "~/assets/icons/viewmode/icon.svg?component"
import TableMode from "~/assets/icons/viewmode/table.svg?component"
import TreeMode from "~/assets/icons/viewmode/tree.svg?component"
import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { PropertyGroup, PropertyId, PropertySchema, PropertyStatus, VirtualProperties } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { PropertyColor, PropertySize, ViewModeType } from "~/classes/generated/interface"
import { sendSelfUpdateRequests } from "~/classes/handlers/user/update"
import { InterfaceUser } from "~/classes/users/InterfaceUser"
import { defaultColumnViewSettings, defaultGalleryViewSettings, defaultTableViewSettings } from "~/utils/flatbuffers/interface"

class ViewMode extends GlobalComposable<{
  /** The current view mode, saved as the "latest view mode"  */
  current: InterfaceViewModeSettings
  /** The global default view mode, saved as the "global view mode"  */
  global?: InterfaceViewModeSettings
  /** The saved defaults per view mode type, for saved settings per mode  */
  byMode: Record<ViewModeType, InterfaceViewModeSettings>
  byFolder: Record<string, InterfaceViewModeSettings>
  /** whether the dropdown menu is visible */
  visible: boolean
}> {
  override defaultState = () => {
    return {
      current: this.defaultSettings(ViewModeType.Icon),
      global: undefined,
      byMode: {
        [ViewModeType.Icon]: this.defaultSettings(ViewModeType.Icon),
        [ViewModeType.Grid]: this.defaultSettings(ViewModeType.Grid),
        [ViewModeType.Feed]: this.defaultSettings(ViewModeType.Feed),
        [ViewModeType.Detail]: this.defaultSettings(ViewModeType.Detail),
        [ViewModeType.Tree]: this.defaultSettings(ViewModeType.Tree),
        [ViewModeType.Column]: this.defaultSettings(ViewModeType.Column),
        [ViewModeType.Table]: this.defaultSettings(ViewModeType.Table),
        [ViewModeType.Gallery]: this.defaultSettings(ViewModeType.Gallery),
      },
      byFolder: {},
      visible: false,
    }
  }

  readonly defaultColumnModeWidth = 280
  readonly defaultTableModeWidth = 280

  defaultSettings = (mode: ViewModeType): InterfaceViewModeSettings => {
    return {
      mode,
      show: [{
        propertyId: PropertyId.FILE_VIRTUAL_PREVIEW,
        size: PropertySize.Medium,
        color: PropertyColor.Default,
        scale: 0.4,
      }, {
        propertyId: PropertyId.FILE_NAME,
        size: PropertySize.Medium,
        color: PropertyColor.Default,
        scale: null,
      }, {
        propertyId: PropertyId.FILE_VIRTUAL_TAGS,
        size: PropertySize.Tiny,
        color: PropertyColor.Default,
        scale: null,
      }, {
        propertyId: PropertyId.FILE_RATING,
        size: PropertySize.Small,
        color: PropertyColor.Amber,
        scale: null,
      }, {
        propertyId: PropertyId.FILE_CREATED_AT,
        size: PropertySize.Small,
        color: PropertyColor.Steel,
        scale: null,
      }],
      sort: [],
      filter: [],
      general: {
        notes: true,
      },
      columnView: defaultColumnViewSettings(),
      detailView: {
        maxColumnWidth: 500,
      },
      tableView: defaultTableViewSettings(),
      galleryView: defaultGalleryViewSettings(),
    }
  }

  clearViewModeState = () => {
    this.state.value = this.defaultState()
  }

  /**
   * Based on changes to our system or bugs, we may need to make changes to the
   * parsed/deserialized view mode settings. For example:
   * 1. On 05/09/2025 a bug was identified that allowed users to hide their
   *    preview property, which is required. We no longer allow the property
   *    to be removed, but we may want to leave this in to prevent future issues
   * @param settings sanitized settings
   */
  private sanitizedViewModeSettings = (settings: InterfaceViewModeSettings) => {
    if (!isDefined(settings.show.find(i => i.propertyId === PropertyId.FILE_VIRTUAL_PREVIEW))) {
      settings.show = [
        {
          propertyId: PropertyId.FILE_VIRTUAL_PREVIEW,
          size: PropertySize.Medium,
          color: PropertyColor.Default,
          scale: 0.4,
        },
        ...settings.show,
      ]
    }
    return settings
  }

  loadViewModeSettingsFromUser = (user: InterfaceUser) => {
    const global = user.property(PropertyId.USER_GLOBAL_DEFAULT_VIEW_MODE)
    if (global && global.value.type === PropertySchema.ViewModeSettingsValue)
      this.state.value.global = this.sanitizedViewModeSettings(global.value.settings)
    const current = user.property(PropertyId.USER_LATEST_VIEW_MODE)
    if (current && current.value.type === PropertySchema.ViewModeSettingsValue)
      this.state.value.current = this.sanitizedViewModeSettings(current.value.settings)
    const iconMode = user.property(PropertyId.USER_LATEST_ICON_VIEW_MODE)
    if (iconMode && iconMode.value.type === PropertySchema.ViewModeSettingsValue)
      this.state.value.byMode[ViewModeType.Icon] = this.sanitizedViewModeSettings(iconMode.value.settings)
    const gridMode = user.property(PropertyId.USER_LATEST_GRID_VIEW_MODE)
    if (gridMode && gridMode.value.type === PropertySchema.ViewModeSettingsValue)
      this.state.value.byMode[ViewModeType.Grid] = this.sanitizedViewModeSettings(gridMode.value.settings)
    const feedMode = user.property(PropertyId.USER_LATEST_FEED_VIEW_MODE)
    if (feedMode && feedMode.value.type === PropertySchema.ViewModeSettingsValue)
      this.state.value.byMode[ViewModeType.Feed] = this.sanitizedViewModeSettings(feedMode.value.settings)
    const detailMode = user.property(PropertyId.USER_LATEST_DETAIL_VIEW_MODE)
    if (detailMode && detailMode.value.type === PropertySchema.ViewModeSettingsValue)
      this.state.value.byMode[ViewModeType.Detail] = this.sanitizedViewModeSettings(detailMode.value.settings)
    const treeMode = user.property(PropertyId.USER_LATEST_TREE_VIEW_MODE)
    if (treeMode && treeMode.value.type === PropertySchema.ViewModeSettingsValue)
      this.state.value.byMode[ViewModeType.Tree] = this.sanitizedViewModeSettings(treeMode.value.settings)
    const columnMode = user.property(PropertyId.USER_LATEST_COLUMN_VIEW_MODE)
    if (columnMode && columnMode.value.type === PropertySchema.ViewModeSettingsValue)
      this.state.value.byMode[ViewModeType.Column] = this.sanitizedViewModeSettings(columnMode.value.settings)
    const tableMode = user.property(PropertyId.USER_LATEST_TABLE_VIEW_MODE)
    if (tableMode && tableMode.value.type === PropertySchema.ViewModeSettingsValue)
      this.state.value.byMode[ViewModeType.Table] = this.sanitizedViewModeSettings(tableMode.value.settings)
    const galleryMode = user.property(PropertyId.USER_LATEST_GALLERY_VIEW_MODE)
    if (galleryMode && galleryMode.value.type === PropertySchema.ViewModeSettingsValue)
      this.state.value.byMode[ViewModeType.Gallery] = this.sanitizedViewModeSettings(galleryMode.value.settings)
  }

  /**
   * Update the user's current view mode settings. This requires we update the
   * latest, as well as the latest for the particular view mode
   */
  protected _persistViewModeSettings = (propertyId: PropertyId) => {
    const { state: myself } = useSelf()
    if (!isDefined(myself.value.user))
      throw new Error("Cannot update view mode settings without a user")

    const timestamp = uuidv7()
    const user = InterfaceUser.blank(myself.value.user.id)
    user.addProperty({
      propertyId,
      id: myself.value.user.id,
      schema: PropertySchema.ViewModeSettingsValue,
      version: 0, // derived
      status: PropertyStatus.StatusPending,
      value: { type: PropertySchema.ViewModeSettingsValue, settings: this.state.value.current },
      timestamp,
    })
    sendSelfUpdateRequests([{
      requestId: generateWorkerRequestId(),
      offline: false,
      user: user.inner(),
    }])
  }

  persistCurrentViewModeSettings = () => {
    return this._persistViewModeSettings(PropertyId.USER_LATEST_VIEW_MODE)
  }

  persistCurrentViewModeSettingsByType = () => {
    let propertyId
    if (this.state.value.current.mode === ViewModeType.Icon)
      propertyId = PropertyId.USER_LATEST_ICON_VIEW_MODE
    else if (this.state.value.current.mode === ViewModeType.Grid)
      propertyId = PropertyId.USER_LATEST_GRID_VIEW_MODE
    else if (this.state.value.current.mode === ViewModeType.Feed)
      propertyId = PropertyId.USER_LATEST_FEED_VIEW_MODE
    else if (this.state.value.current.mode === ViewModeType.Detail)
      propertyId = PropertyId.USER_LATEST_DETAIL_VIEW_MODE
    else if (this.state.value.current.mode === ViewModeType.Tree)
      propertyId = PropertyId.USER_LATEST_TREE_VIEW_MODE
    else if (this.state.value.current.mode === ViewModeType.Column)
      propertyId = PropertyId.USER_LATEST_COLUMN_VIEW_MODE
    else if (this.state.value.current.mode === ViewModeType.Table)
      propertyId = PropertyId.USER_LATEST_TABLE_VIEW_MODE
    else if (this.state.value.current.mode === ViewModeType.Gallery)
      propertyId = PropertyId.USER_LATEST_GALLERY_VIEW_MODE
    else
      throw new Error("Unknown view mode type")
    return this._persistViewModeSettings(propertyId)
  }

  /**
   * NOTE: You should likely call `switchCollectionViewMode` from
   * useCollectionView() instead of this, as it cleans up state
   * in the collection view.
   */
  switchViewMode = (mode: InterfaceViewModeType) => {
    this.state.value.current.mode = mode
    this.persistCurrentViewModeSettings()
  }

  /** Gets the property show options iff the current property is being shown, else undefined */
  propertyShowOptions = (propertyId: PropertyId) => {
    for (const prop of this.state.value.current.show)
      if (prop.propertyId === propertyId)
        return prop
  }

  isShowingProperty = (propertyId: PropertyId) => {
    return this.propertyShowOptions(propertyId) !== undefined
  }

  showViewModeProperty = (
    propertyId: PropertyId,
    color?: PropertyColor,
    size?: PropertySize,
  ) => {
    if (this.isShowingProperty(propertyId))
      return this.error("Already showing view mode property")
    const { capture } = useAnalytics()

    size = size ?? PropertySize.Small

    // default colors are different per property
    if (!isDefined(color)) {
      if (propertyId === PropertyId.FILE_RATING)
        color = PropertyColor.Amber
      else if (propertyId === PropertyId.FILE_CREATED_AT)
        color = PropertyColor.Steel
      else if (propertyId === PropertyId.FILE_MODIFIED_AT)
        color = PropertyColor.Steel
      else if (propertyId === PropertyId.FILE_ACCESSED_AT)
        color = PropertyColor.Steel
      else
        color = PropertyColor.Default
    }

    // NOTE: for some reason, if we directly edit the state object here
    // it doesn't trigger reactivity updates, so we clone the object and
    // assign the value to it, ensuring reactivity updates.
    const newState = clone(this.state.value)
    newState.current.show.push({ propertyId, color, size, scale: null })
    this.state.value = newState

    this.persistCurrentViewModeSettings()
    this.persistCurrentViewModeSettingsByType()
    capture("user_added_view_mode_property", { propertyId })
  }

  hideViewModeProperty = (propertyId: PropertyId) => {
    const { capture } = useAnalytics()

    if (propertyId === PropertyId.FILE_VIRTUAL_PREVIEW)
      return this.error("Cannot hide preview property")

    // NOTE: for some reason, if we directly edit the state object here
    // it doesn't trigger reactivity updates, so we clone the object and
    // assign the value to it, ensuring reactivity updates.
    const newState = clone(this.state.value)
    newState.current.show = newState.current.show.filter(p => p.propertyId !== propertyId)
    this.state.value = newState

    this.persistCurrentViewModeSettings()
    this.persistCurrentViewModeSettingsByType()
    capture("user_removed_view_mode_property", { propertyId })
  }

  /** NOTE: this works primarily because the preview cannot be moved */
  rearrangeViewModeProperty = (oldIndex: number, newIndex: number) => {
    if (oldIndex < 0)
      throw new Error("Cannot re-arrange as property is not shown")
    if (newIndex < oldIndex)
      newIndex += 1
    if (newIndex === oldIndex)
      return this.warn("Rearranged property is already at index")
    const removed = this.state.value.current.show.splice(oldIndex, 1)
    this.state.value.current.show.splice(newIndex, 0, ...removed)

    this.persistCurrentViewModeSettings()
    this.persistCurrentViewModeSettingsByType()
  }

  protected _previewScale = () => {
    const previewProp = this.state.value.current.show.find(i => i.propertyId === PropertyId.FILE_VIRTUAL_PREVIEW)
    if (!previewProp || previewProp.scale === null)
      throw new Error("No preview property scale in view modes")
    return previewProp.scale
  }

  previewScale = computed(this._previewScale)

  /**
   * Updates the preview scale, but can only be between 0 and 1
   *
   * NOTE: do not use this in real-time as it updates a user property on each call
   */
  updatePreviewScale = (value: number, persist = false) => {
    if (value < 0)
      value = 0
    if (value > 1)
      value = 1
    this.state.value.current.show[0].scale = value

    if (persist) {
      this.persistCurrentViewModeSettings()
      this.persistCurrentViewModeSettingsByType()
    }
  }

  updatePropertyColor = (propertyId: PropertyId, color: PropertyColor, persist = false) => {
    const prop = this.propertyShowOptions(propertyId)
    if (!prop)
      throw new Error("Cannot update property color as property is not shown")
    prop.color = color
    if (persist) {
      this.persistCurrentViewModeSettings()
      this.persistCurrentViewModeSettingsByType()
    }
  }

  updatePropertySize = (propertyId: PropertyId, size: PropertySize, persist = false) => {
    const prop = this.propertyShowOptions(propertyId)
    if (!prop)
      throw new Error("Cannot update property size as property is not shown")
    prop.size = size
    if (persist) {
      this.persistCurrentViewModeSettings()
      this.persistCurrentViewModeSettingsByType()
    }
  }

  /**
   * persist tells it to get persisted to the server, which you don't
   * want to do in the render loop
   */
  updateColumnViewSettings = (
    settings: Partial<InterfaceViewModeColumnViewSettings>,
    persist = false,
  ) => {
    if (this.state.value.current.mode !== ViewModeType.Column)
      throw new Error("Must be in column view mode")
    this.state.value.current.columnView = {
      ...defaultColumnViewSettings(),
      ...settings,
    }

    if (persist) {
      this.persistCurrentViewModeSettings()
      this.persistCurrentViewModeSettingsByType()
    }
  }

  /**
   * persist tells it to get persisted to the server, which you don't
   * want to do in the render loop
   */
  updateTableViewSettings = (
    settings: Partial<InterfaceViewModeTableViewSettings>,
    persist = false,
  ) => {
    if (this.state.value.current.mode !== ViewModeType.Table)
      throw new Error("Must be in table view mode")
    this.state.value.current.tableView = {
      ...defaultTableViewSettings(),
      ...settings,
    }
    if (persist) {
      this.persistCurrentViewModeSettings()
      this.persistCurrentViewModeSettingsByType()
    }
  }

  /**
   * persist tells it to get persisted to the server, which you don't
   * want to do in the render loop
   */
  updateGalleryViewSettings = (
    settings: Partial<InterfaceViewModeGalleryViewSettings>,
    persist = false,
  ) => {
    if (this.state.value.current.mode !== ViewModeType.Gallery)
      throw new Error("Must be in gallery view mode")
    this.state.value.current.galleryView = {
      ...defaultGalleryViewSettings(),
      ...settings,
    }
    if (persist) {
      this.persistCurrentViewModeSettings()
      this.persistCurrentViewModeSettingsByType()
    }
  }

  // ============================================================================
  // SORTING
  // ============================================================================

  propertySortOptions = (propertyId: PropertyId) => {
    for (const prop of this.state.value.current.sort)
      if (prop.propertyId === propertyId)
        return prop
  }

  isSortingProperty = (propertyId: PropertyId) => {
    return this.propertySortOptions(propertyId) !== undefined
  }

  /** Used to show a sort badge in the UI. In searches this excludes the search relevance */
  isCustomSort = () => {
    return this.state
      .value
      .current
      .sort
      .length > 0
  }

  addViewModeSort = (sort: InterfaceViewModeSort) => {
    if (this.isSortingProperty(sort.propertyId))
      return this.error("Already sorting by view mode property")
    this.state.value.current.sort = [
      ...this.state.value.current.sort,
      sort,
    ]

    this.persistCurrentViewModeSettings()
    this.persistCurrentViewModeSettingsByType()
  }

  changeViewModeSort = (sort: InterfaceViewModeSort) => {
    const idx = this.state.value.current.sort.findIndex(i => i.propertyId === sort.propertyId)
    if (idx < 0)
      return this.error("Not sorting by view mode property")
    this.state.value.current.sort[idx] = sort

    this.persistCurrentViewModeSettings()
    this.persistCurrentViewModeSettingsByType()
  }

  removeViewModeSort = (propertyId: string) => {
    this.state.value.current.sort = this.state.value.current.sort.filter(
      p => p.propertyId !== propertyId,
    )

    this.persistCurrentViewModeSettings()
    this.persistCurrentViewModeSettingsByType()
  }

  clearViewModeSorts = () => {
    this.state.value.current.sort = []

    this.persistCurrentViewModeSettings()
    this.persistCurrentViewModeSettingsByType()
  }

  // ============================================================================
  // FILTERING
  // ============================================================================

  isCustomFilter = () => {
    return this.state
      .value
      .current
      .filter
      .length > 0
  }

  addViewModeFilter = (filter: InterfaceViewModeFilter) => {
    if (this.isFilteringProperty(filter.propertyId, filter.type))
      return this.error("Already sorting by view mode property")
    this.state.value.current.filter = [...this.state.value.current.filter, filter]

    this.persistCurrentViewModeSettings()
    this.persistCurrentViewModeSettingsByType()
  }

  removeViewModeFilter = (propertyId: string) => {
    this.state.value.current.filter = this.state.value.current.filter.filter(
      p => p.propertyId !== propertyId,
    )

    this.persistCurrentViewModeSettings()
    this.persistCurrentViewModeSettingsByType()
  }

  propertyFilterOptions = (propertyId: PropertyId, type?: PropertyFilterType) => {
    for (const prop of this.state.value.current.filter) {
      if (isDefined(type) && prop.type !== type)
        continue
      else if (prop.propertyId === propertyId)
        return prop
    }
  }

  isFilteringProperty = (propertyId: PropertyId, type?: PropertyFilterType) => {
    return this.propertyFilterOptions(propertyId, type) !== undefined
  }

  clearViewModeFilters = () => {
    this.state.value.current.filter = []
    this.persistCurrentViewModeSettings()
    this.persistCurrentViewModeSettingsByType()
  }

  /** Turns the current view mode into a selection for folder list requests */
  currentViewModeStateAsSelection = () => {
    const properties = []
    const groups = []
    for (const show of this.state.value.current.show) {
      if (VirtualProperties.has(show.propertyId)) {
        if (show.propertyId === PropertyId.FILE_VIRTUAL_PREVIEW)
          continue
        else if (show.propertyId === PropertyId.FILE_VIRTUAL_DIMENSIONS)
          properties.push(PropertyId.MEDIA_HEIGHT_PX, PropertyId.MEDIA_WIDTH_PX)
        else if (show.propertyId === PropertyId.FILE_VIRTUAL_TAGS)
          groups.push(PropertyGroup.FileTagMetaProperty)
        else if (show.propertyId === PropertyId.FILE_VIRTUAL_PATH)
          continue
      }
      else {
        properties.push(show.propertyId)
      }
    }
    const selection = InterfaceSelection.minimumInterfaceSelection()
    for (const group of groups)
      selection.addEntireGroup(group)
    for (const property of properties)
      selection.addPropertyId(property)
    return selection
  }

  /**
   * FIXME: uncomment this when all modes are re-implemented
   */
  readonly viewModes: Record<ViewModeType, {
    name: string
    icon: Component
    type: ViewModeType
  }> = {
      [ViewModeType.Icon]: { type: ViewModeType.Icon, name: "Icon", icon: IconMode },
      [ViewModeType.Grid]: { type: ViewModeType.Grid, name: "Grid", icon: GridMode },
      [ViewModeType.Feed]: { type: ViewModeType.Feed, name: "Feed", icon: FeedMode },
      [ViewModeType.Detail]: { type: ViewModeType.Detail, name: "Detail", icon: DetailMode },
      [ViewModeType.Tree]: { type: ViewModeType.Tree, name: "Tree", icon: TreeMode },
      [ViewModeType.Column]: { type: ViewModeType.Column, name: "Column", icon: ColumnMode },
      [ViewModeType.Table]: { type: ViewModeType.Table, name: "Table", icon: TableMode },
      [ViewModeType.Gallery]: { type: ViewModeType.Gallery, name: "Gallery", icon: GalleryMode },
    }

  readonly showableProperties: Partial<Record<PropertyId, {
    name: string
    propertyId: PropertyId
    editable?: boolean
    sortable?: boolean
    filterable?: boolean
    schema?: PropertySchema
  }>> = {
      [PropertyId.FILE_VIRTUAL_PREVIEW]: {
        name: "Preview",
        propertyId: PropertyId.FILE_VIRTUAL_PREVIEW,
      },
      [PropertyId.FILE_NAME]: {
        name: "Name",
        propertyId: PropertyId.FILE_NAME,
        editable: true,
        sortable: true,
        filterable: true,
        schema: PropertySchema.NonEmptyStringValue,
      },
      [PropertyId.FILE_VIRTUAL_PATH]: {
        name: "Path",
        propertyId: PropertyId.FILE_VIRTUAL_PATH,
      },
      [PropertyId.FILE_SIZE]: {
        name: "File size",
        propertyId: PropertyId.FILE_SIZE,
        sortable: true,
        filterable: true,
        schema: PropertySchema.SizeBytesValue,
      },
      [PropertyId.FILE_CREATED_AT]: {
        name: "Created at",
        propertyId: PropertyId.FILE_CREATED_AT,
        sortable: true,
        filterable: true,
        schema: PropertySchema.UtcTimestampMillisValue,
      },
      [PropertyId.FILE_MODIFIED_AT]: {
        name: "Modified at",
        propertyId: PropertyId.FILE_MODIFIED_AT,
        sortable: true,
        filterable: true,
        schema: PropertySchema.UtcTimestampMillisValue,
      },
      [PropertyId.FILE_ACCESSED_AT]: {
        name: "Accessed at",
        propertyId: PropertyId.FILE_ACCESSED_AT,
        sortable: true,
        filterable: true,
        schema: PropertySchema.UtcTimestampMillisValue,
      },
      /**
       * For this property, we don't let it be sorted on since the server
       * doesn't know how to sort by this property, causing a potential
       * infinite loop.
       * TODO: allow server to sort by this property
       */
      [PropertyId.FILE_VIRTUAL_ADDED_AT]: {
        name: "Moved at",
        propertyId: PropertyId.FILE_VIRTUAL_ADDED_AT,
        sortable: false,
        filterable: true,
        schema: PropertySchema.UtcTimestampMillisValue,
      },
      [PropertyId.FILE_MIME_TYPE]: {
        name: "MIME Type",
        propertyId: PropertyId.FILE_MIME_TYPE,
        editable: false,
        sortable: true,
        filterable: true,
        schema: PropertySchema.MimeTypeValue,
      },
      [PropertyId.FILE_VIRTUAL_MEDIA_TYPE]: {
        name: "Media Kind",
        propertyId: PropertyId.FILE_VIRTUAL_MEDIA_TYPE,
        editable: false,
        sortable: false,
        filterable: true,
        schema: PropertySchema.RawBytesValue,
      },
      [PropertyId.MEDIA_PAGES]: {
        name: "Pages",
        propertyId: PropertyId.MEDIA_PAGES,
        editable: false,
        sortable: true,
        filterable: true,
        schema: PropertySchema.PositiveIntegerValue,
      },
      [PropertyId.MEDIA_WIDTH_PX]: {
        name: "Width (px)",
        propertyId: PropertyId.MEDIA_WIDTH_PX,
        editable: false,
        sortable: true,
        filterable: true,
        schema: PropertySchema.PositiveIntegerValue,
      },
      [PropertyId.MEDIA_HEIGHT_PX]: {
        name: "Height (px)",
        propertyId: PropertyId.MEDIA_HEIGHT_PX,
        editable: false,
        sortable: true,
        filterable: true,
        schema: PropertySchema.PositiveIntegerValue,
      },
      [PropertyId.FILE_VIRTUAL_DIMENSIONS]: {
        name: "Dimensions (W:H)",
        propertyId: PropertyId.FILE_VIRTUAL_DIMENSIONS,
        sortable: true,
        filterable: true,
        schema: PropertySchema.PositiveFloatValue,
      },
      [PropertyId.MEDIA_WIDTH_CM]: {
        name: "Phys. Width (cm)",
        propertyId: PropertyId.MEDIA_WIDTH_CM,
        editable: false,
        sortable: true,
        filterable: true,
        schema: PropertySchema.PositiveFloatValue,
      },
      [PropertyId.MEDIA_HEIGHT_CM]: {
        name: "Phys. Height (cm)",
        propertyId: PropertyId.MEDIA_HEIGHT_CM,
        editable: false,
        sortable: true,
        filterable: true,
        schema: PropertySchema.PositiveFloatValue,
      },
      [PropertyId.MEDIA_DURATION_MS]: {
        name: "Duration (ms)",
        propertyId: PropertyId.MEDIA_DURATION_MS,
        editable: false,
        sortable: true,
        filterable: true,
        schema: PropertySchema.NonNegativeIntegerValue,
      },
      [PropertyId.FILE_VIRTUAL_TAGS]: {
        name: "Tags",
        propertyId: PropertyId.FILE_VIRTUAL_TAGS,
        editable: true,
        filterable: true,
        schema: PropertySchema.HashableTagValue,
      },
      [PropertyId.FILE_NOTES]: {
        name: "Notes",
        propertyId: PropertyId.FILE_NOTES,
        editable: true,
        filterable: true,
        schema: PropertySchema.AnyStringValue,
      },
      [PropertyId.FILE_RATING]: {
        name: "Rating",
        propertyId: PropertyId.FILE_RATING,
        editable: true,
        sortable: true,
        filterable: true,
        schema: PropertySchema.PositiveFloatValue,
      },
      [PropertyId.FILE_GENERATION_PROMPT]: {
        name: "Gen. Prompt",
        propertyId: PropertyId.FILE_GENERATION_PROMPT,
        editable: true,
        sortable: false,
        filterable: true,
        schema: PropertySchema.NonEmptyStringValue,
      },
      [PropertyId.FILE_GENERATION_NEGATIVE_PROMPT]: {
        name: "Gen. Neg. Prompt",
        propertyId: PropertyId.FILE_GENERATION_NEGATIVE_PROMPT,
        editable: true,
        sortable: false,
        filterable: true,
        schema: PropertySchema.NonEmptyStringValue,
      },
      [PropertyId.FILE_GENERATION_SOURCE]: {
        name: "Gen. Source",
        propertyId: PropertyId.FILE_GENERATION_SOURCE,
        editable: true,
        sortable: true,
        filterable: true,
        schema: PropertySchema.NonEmptyStringValue,
      },
      [PropertyId.FILE_GENERATION_MODEL]: {
        name: "Gen. Model",
        propertyId: PropertyId.FILE_GENERATION_MODEL,
        editable: true,
        sortable: true,
        filterable: true,
        schema: PropertySchema.NonEmptyStringValue,
      },
      [PropertyId.FILE_GENERATION_SEED]: {
        name: "Gen. Seed",
        propertyId: PropertyId.FILE_GENERATION_SEED,
        editable: true,
        sortable: false,
        filterable: true,
        schema: PropertySchema.AnyIntegerValue,
      },
    }

  readonly viewModePropertySizes: Record<PropertySize, {
    textCss: string
    singleLineCss: string
    label: string
  }> = {
      [PropertySize.Tiny]: {
        textCss: "t-property-xs",
        singleLineCss: "h-$page-properties-xs-h-single-outer overflow-hidden",
        label: "Tiny",
      },
      [PropertySize.Small]: {
        textCss: "t-property-sm",
        singleLineCss: "h-$page-properties-sm-h-single-outer overflow-hidden",
        label: "Small",
      },
      [PropertySize.Medium]: {
        textCss: "t-property-md",
        singleLineCss: "h-$page-properties-md-h-single-outer overflow-hidden",
        label: "Medium",
      },
      [PropertySize.Large]: {
        textCss: "t-property-lg",
        singleLineCss: "h-$page-properties-lg-h-single-outer overflow-hidden",
        label: "Large",
      },
      [PropertySize.Huge]: {
        textCss: "t-property-xl",
        singleLineCss: "h-$page-properties-xl-h-single-outer overflow-hidden",
        label: "Huge",
      },
      [PropertySize.Giant]: {
        textCss: "t-property-2xl",
        singleLineCss: "h-$page-properties-2xl-h-single-outer overflow-hidden",
        label: "Giant",
      },
    }

  readonly viewModePropertyColors: Record<PropertyColor, {
    textCss: string
    bgCss: string
    label: string
  }> = {
      [PropertyColor.Default]: {
        textCss: "text-$global-t-default",
        label: "Default",
        bgCss: "bg-$page-properties-bg-property",
      },
      [PropertyColor.Steel]: {
        textCss: "text-$global-steel-primary",
        label: "Steel",
        bgCss: "bg-$global-steel-accent",
      },
      [PropertyColor.Amber]: {
        textCss: "text-$global-amber-primary",
        label: "Amber",
        bgCss: "bg-$global-amber-accent",
      },
      [PropertyColor.Green]: {
        textCss: "text-$global-green-primary",
        label: "Green",
        bgCss: "bg-$global-green-accent",
      },
      [PropertyColor.Teal]: {
        textCss: "text-$global-teal-primary",
        label: "Teal",
        bgCss: "bg-$global-teal-accent",
      },
      [PropertyColor.Blue]: {
        textCss: "text-$global-blue-primary",
        label: "Blue",
        bgCss: "bg-$global-blue-accent",
      },
      [PropertyColor.Purple]: {
        textCss: "text-$global-purple-primary",
        label: "Purple",
        bgCss: "bg-$global-purple-accent",
      },
      [PropertyColor.Violet]: {
        textCss: "text-$global-violet-primary",
        label: "Violet",
        bgCss: "bg-$global-violet-accent",
      },
      [PropertyColor.Pink]: {
        textCss: "text-$global-pink-primary",
        label: "Pink",
        bgCss: "bg-$global-pink-accent",
      },
      [PropertyColor.Red]: {
        textCss: "text-$global-red-primary",
        label: "Red",
        bgCss: "bg-$global-red-accent",
      },
    }
}

const viewMode = new ViewMode("view-mode", theme.colors.teal.hex)
export default function () {
  return viewMode.use()
}
